(() => {
  'use strict';
  if (window.CedarAmberMotion) { window.CedarAmberMotion.init(); return; }
  const instances = new Map();
  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const ease = value => 1 - Math.pow(1 - clamp(value), 3);
  const EASE = 'cubic-bezier(.22,1,.36,1)';

  // These calculations are independent of the DOM so short-screen fit and
  // forward/reverse handoff boundaries can be checked without a browser clock.
  function photoLayout(width, height, copyHeight, profile) {
    const phone = width < 750;
    const compositionWidth = Math.min(width, 1440);
    const outer = (width - compositionWidth) / 2;
    const gutter = phone ? (width < 375 ? 18 : 22) : clamp(compositionWidth * .05, 32, 72);
    const gap = phone ? 18 : clamp(compositionWidth * .045, 32, 72);
    const copyWidth = phone ? width - gutter * 2 : Math.min(430, compositionWidth * .32);
    const size = phone
      ? Math.min(width - gutter * 2, height - copyHeight - gutter * 2 - gap)
      : Math.min(height - gutter * 2, compositionWidth - gutter * 2 - gap - copyWidth);
    const leftPhoto = ['barrel', 'indoor'].includes(profile);
    return {
      fits: height >= 310 && size >= 165 && copyHeight <= height - gutter * 2,
      size,
      x: phone ? (width - size) / 2 : outer + (leftPhoto ? gutter : compositionWidth - gutter - size),
      y: phone ? gutter : (height - size) / 2,
      copyX: phone ? gutter : outer + (leftPhoto ? compositionWidth - gutter - copyWidth : gutter),
      copyY: phone ? gutter + size + gap : (height - copyHeight) / 2,
      copyWidth,
      travel: Math.min(640, Math.max(260, height * .82))
    };
  }

  function photoState(progress, available = true) {
    const p = clamp(progress);
    if (!available) return { setting: 1, settingCopy: 1, scale: 1, front: 0, copy: 0, phase: 'fallback' };
    const front = ease((p - .25) / .31);
    return {
      setting: 1 - ease((p - .28) / .32),
      settingCopy: 1 - ease((p - .21) / .065),
      scale: 1.045 - ease(p / .62) * .045,
      front,
      copy: ease((p - .58) / .17),
      phase: p < .25 ? 'setting' : p < .58 ? 'handoff' : 'form'
    };
  }

  function approach(current, target, elapsed) {
    if (current === null || Math.abs(current - target) < .0004) return target;
    return current + (target - current) * (1 - Math.exp(-Math.min(elapsed, 50) / 72));
  }

  function mount(root) {
    if (instances.has(root) || !root.querySelector) return;
    const life = new AbortController();
    const on = (node, type, handler, options = {}) => node?.addEventListener(type, handler, { ...options, signal: life.signal });
    const all = (selector, context = root) => [...context.querySelectorAll(selector)];
    const animations = new Map();
    const reveals = new Map();
    const headings = new Map();
    const depth = [];
    const activeDepth = new Set();
    const cinematic = root.classList.contains('ca-cinematic-product');
    const theatres = [];
    const alignments = [];
    const bottomBars = all('[data-ca-sticky], [data-ca-mobile-choose]');
    const reading = root.querySelector('.ca-pdp-reading-progress');
    const opening = root.querySelector('.ca-f-stage');
    const nav = root.querySelector('.ca-pdp-section-nav');
    const rail = nav?.querySelector(':scope > div');
    const links = all('[data-ca-section-link]');
    const marker = rail ? document.createElement('span') : null;
    let frame = 0, dirty = true, destroyed = false, activeChapter = '', manualRailUntil = 0, lastTime = 0;
    let viewport = innerHeight, narrow = innerWidth < 750, header = 116, navHeight = 0;
    let rootTop = 0, rootHeight = 1, chapters = [];
    let revealObserver, depthObserver, theatreObserver, resizeObserver, mutationObserver;

    function animate(element, keyframes, options = {}) {
      if (destroyed || typeof element.animate !== 'function') return;
      const animation = element.animate(keyframes, { duration: 720, easing: EASE, fill: 'backwards', ...options });
      if (!animations.has(element)) animations.set(element, new Set());
      animations.get(element).add(animation);
      animation.finished.catch(() => {}).finally(() => {
        animations.get(element)?.delete(animation);
        if (!animations.get(element)?.size) animations.delete(element);
      });
      return animation;
    }

    function settleWithin(target) {
      for (const [element, running] of animations) {
        if (element === target || element.contains(target) || target.contains(element)) {
          for (const animation of running) { try { animation.finish(); } catch { animation.cancel(); } }
        }
      }
      for (const [element, record] of reveals) {
        if (element === target || target.contains(element)) { record.seen = true; revealObserver?.unobserve(element); }
      }
    }

    function splitHeading(heading) {
      if (headings.has(heading) || heading.textContent.trim().length > 150 || heading.querySelector('a,button,input')) return;
      headings.set(heading, heading.innerHTML);
      const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);
      textNodes.forEach(node => {
        const fragment = document.createDocumentFragment();
        node.textContent.split(/(\s+)/u).forEach(part => {
          if (!part) return;
          if (/^\s+$/u.test(part)) fragment.append(document.createTextNode(part));
          else { const word = document.createElement('span'); word.className = 'ca-m-word'; word.textContent = part; fragment.append(word); }
        });
        node.replaceWith(fragment);
      });
    }

    function reveal(record) {
      const { element, kind, order } = record;
      if (record.seen || element.closest('[hidden], [inert]')) return;
      record.seen = true;
      revealObserver?.unobserve(element);
      if (kind === 'hero') {
        animate(element, [{ translate: `0 ${narrow ? 7 : 12}px`, scale: '.992' }, { translate: '0 0', scale: '1' }], { duration: 900 });
      } else if (kind === 'title' || kind === 'purchase') {
        animate(element, [{ translate: '0 8px' }, { translate: '0 0' }], { duration: 520 });
      } else if (kind === 'heading') {
        const words = all('.ca-m-word', element);
        const positions = words.map(word => ({ word, top: word.getBoundingClientRect().top }));
        const rows = [];
        positions.forEach(({ word, top }) => {
          let row = rows.findIndex(value => Math.abs(value - top) < 5);
          if (row < 0) { rows.push(top); row = rows.length - 1; }
          animate(word, [{ opacity: .42, translate: `0 ${narrow ? 15 : 24}px` }, { opacity: 1, translate: '0 0' }], { duration: 900, delay: Math.min(row, 3) * 85 });
        });
        if (!words.length) animate(element, [{ opacity: .6, translate: '0 16px' }, { opacity: 1, translate: '0 0' }]);
      } else if (kind === 'photo') {
        animate(element, [{ opacity: .68, translate: `0 ${narrow ? 16 : 28}px`, scale: '.985' }, { opacity: 1, translate: '0 0', scale: '1' }], { duration: 1050, delay: Math.min(order, 3) * 55 });
      } else if (kind === 'rule') {
        element.style.setProperty('--ca-m-rule', '1');
        animate(element, [{ opacity: .65, translate: '0 10px' }, { opacity: 1, translate: '0 0' }], { duration: 650, delay: Math.min(order, 4) * 45 });
      } else {
        animate(element, [{ opacity: .55, translate: `0 ${narrow ? 12 : 20}px` }, { opacity: 1, translate: '0 0' }], { duration: 760, delay: Math.min(order, 4) * 45 });
      }
    }

    function register(element, kind = 'copy', order = 0) {
      if (!element || reveals.has(element)) return;
      if (kind === 'heading') splitHeading(element);
      const record = { element, kind, order, seen: false };
      reveals.set(element, record);
      if (revealObserver) revealObserver.observe(element);
      else reveal(record);
    }

    function addDepth(selector, { range = 24, aperture = false, scale = 1.055, detail = false } = {}) {
      all(selector).forEach(element => {
        const planes = all('img', element);
        if (!planes.length) return;
        element.dataset.caMDepth = '';
        planes.forEach(image => { image.dataset.caMPlane = ''; });
        if (aperture) all('picture', element).forEach(picture => { picture.dataset.caMAperture = ''; });
        const record = { element, planes, range, aperture, scale, detail, top: 0, height: 1, value: null };
        depth.push(record);
        if (depthObserver) depthObserver.observe(element);
        else activeDepth.add(record);
      });
    }

    function primeTheatre(record) {
      if (record.primed) return;
      record.primed = true;
      const image = record.frontImage;
      image.loading = 'eager';
      image.fetchPriority = 'low';
      const ready = () => {
        const source = image.currentSrc || image.src;
        if (!image.complete || !image.naturalWidth) return;
        const decoded = typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
        decoded.then(() => {
          if (destroyed || !image.naturalWidth || source !== (image.currentSrc || image.src)) return;
          if (!record.imageReady) record.value = Math.min(record.value ?? 0, .18);
          record.imageReady = true;
          queue();
        });
      };
      on(image, 'load', ready);
      on(image, 'error', () => { record.imageReady = false; queue(); });
      ready();
    }

    function setAccessible(record, node, visible) {
      if (!node) return;
      if (!record.accessibility.has(node)) record.accessibility.set(node, { inert: node.hasAttribute('inert'), aria: node.getAttribute('aria-hidden'), visible: null });
      const original = record.accessibility.get(node);
      if (original.visible === visible) return;
      original.visible = visible;
      if (visible) {
        node.toggleAttribute('inert', original.inert);
        if (original.aria === null) node.removeAttribute('aria-hidden'); else node.setAttribute('aria-hidden', original.aria);
      } else {
        node.setAttribute('inert', '');
        node.setAttribute('aria-hidden', 'true');
      }
    }

    function registerTheatres() {
      if (!cinematic) return;
      all('[data-ca-photo-theatre]').forEach(element => {
        const front = element.querySelector('[data-ca-photo-front]');
        const frontImage = front?.querySelector('img');
        const copy = element.querySelector('[data-ca-photo-front-copy]');
        if (!frontImage || !copy) return;
        const record = {
          element, front, frontImage, copy,
          setting: element.querySelector('[data-ca-photo-setting]'),
          settingCopy: element.querySelector('[data-ca-photo-setting-copy]'),
          accessibility: new Map(), top: 0, height: 0, travel: 1,
          value: null, painted: null, enabled: false, primed: false, imageReady: false, keyboardPhase: null
        };
        theatres.push(record);
        if (theatreObserver) theatreObserver.observe(element); else primeTheatre(record);
        setAccessible(record, front, false);
        setAccessible(record, copy, false);
        on(element, 'focusin', event => {
          if (!event.target.matches(':focus-visible')) return;
          record.keyboardPhase = front.contains(event.target) || copy.contains(event.target) ? 1 : 0;
          record.value = record.keyboardPhase;
          if (record.enabled) paintTheatre(record, record.value);
          queue();
        });
        on(element, 'focusout', () => queueMicrotask(() => {
          if (!element.contains(document.activeElement)) { record.keyboardPhase = null; queue(); }
        }));
        on(element, 'pointerdown', () => { record.keyboardPhase = null; queue(); }, { passive: true });
      });
      all('.ca-f-hardware-photo').forEach(element => alignments.push({ element, top: 0, height: 1, value: null, painted: null }));
    }

    function measure() {
      viewport = innerHeight;
      narrow = innerWidth < 750;
      header = parseFloat(getComputedStyle(root).getPropertyValue('--ca-header-height')) || 116;
      navHeight = nav?.getBoundingClientRect().height || 0;
      const scroll = window.scrollY;
      const barHeight = Math.max(0, ...bottomBars.map(bar => bar.getBoundingClientRect().height));
      const stageHeight = Math.min(900, viewport - header - navHeight - (barHeight > 0 ? barHeight + 8 : narrow ? 94 : 88));
      const widths = theatres.map(record => record.element.getBoundingClientRect().width);
      root.style.setProperty('--ca-m-nav-height', `${navHeight}px`);
      // A resize/layout pass may measure the caption after assigning its final
      // width. Scroll frames below consume cached coordinates only.
      theatres.forEach((record, index) => {
        const preliminary = photoLayout(widths[index], stageHeight, 0, root.dataset.caCinematicProfile);
        record.element.style.setProperty('--cp-copy-w', `${preliminary.copyWidth.toFixed(2)}px`);
        record.element.toggleAttribute('data-ca-theatre-compact', narrow && stageHeight < 430);
        record.element.dataset.caTheatreMeasuring = '';
      });
      const layouts = theatres.map((record, index) => photoLayout(widths[index], stageHeight, record.copy.getBoundingClientRect().height, root.dataset.caCinematicProfile));
      theatres.forEach((record, index) => {
        const layout = layouts[index];
        record.enabled = layout.fits;
        record.travel = layout.travel;
        record.element.toggleAttribute('data-ca-theatre-ready', layout.fits);
        delete record.element.dataset.caTheatreMeasuring;
        const values = {
          '--cp-stage-h': stageHeight, '--cp-travel': layout.travel,
          '--cp-front-size': layout.size, '--cp-front-x': layout.x, '--cp-front-y': layout.y,
          '--cp-copy-x': layout.copyX, '--cp-copy-y': layout.copyY, '--cp-copy-w': layout.copyWidth
        };
        Object.entries(values).forEach(([name, value]) => record.element.style.setProperty(name, `${value.toFixed(2)}px`));
        if (!layout.fits) {
          record.element.dataset.caTheatreState = 'static';
          record.element.classList.remove('ca-cp-active');
          setAccessible(record, record.setting, true);
          setAccessible(record, record.settingCopy, true);
          setAccessible(record, record.front, false);
          setAccessible(record, record.copy, false);
        }
        record.painted = null;
      });
      const bounds = root.getBoundingClientRect();
      rootTop = bounds.top + scroll;
      rootHeight = bounds.height;
      depth.forEach(record => { const rect = record.element.getBoundingClientRect(); record.top = rect.top + scroll; record.height = rect.height; });
      [...theatres, ...alignments].forEach(record => { const rect = record.element.getBoundingClientRect(); record.top = rect.top + scroll; record.height = rect.height; });
      const railRect = rail?.getBoundingClientRect();
      chapters = links.map(link => {
        const id = link.getAttribute('href')?.slice(1);
        const target = id && document.getElementById(id);
        if (!target || !root.contains(target)) return null;
        const rect = target.getBoundingClientRect();
        const linkRect = link.getBoundingClientRect();
        return { link, target, key: link.dataset.caSectionLink, top: rect.top + scroll, left: linkRect.left - railRect.left + rail.scrollLeft, width: linkRect.width };
      }).filter(Boolean).sort((a, b) => a.top - b.top);
      dirty = false;
    }

    function updateChapter(scroll) {
      if (!chapters.length) return;
      const position = scroll + header + navHeight + 28;
      let index = 0;
      chapters.forEach((chapter, i) => { if (chapter.top <= position) index = i; });
      const current = chapters[index];
      const end = chapters[index + 1]?.top || rootTop + rootHeight;
      const progress = clamp((position - current.top) / Math.max(1, end - current.top));
      marker.style.setProperty('--ca-m-chapter-progress', Math.max(.06, progress).toFixed(4));
      marker.style.setProperty('--ca-m-marker-x', `${current.left.toFixed(2)}px`);
      marker.style.setProperty('--ca-m-marker-width', current.width.toFixed(2));
      if (activeChapter !== current.key) {
        activeChapter = current.key;
        root.dataset.caMotionActiveChapter = current.key;
        links.forEach(link => { if (link === current.link) link.setAttribute('aria-current', 'location'); else link.removeAttribute('aria-current'); });
        if (performance.now() > manualRailUntil && !rail.contains(document.activeElement)) {
          const left = current.left, right = left + current.width;
          if (left < rail.scrollLeft + 6 || right > rail.scrollLeft + rail.clientWidth - 6) {
            rail.scrollTo({ left: clamp(left - (rail.clientWidth - current.width) / 2, 0, Math.max(0, rail.scrollWidth - rail.clientWidth)), behavior: 'smooth' });
          }
        }
      }
      if (reading) reading.style.transform = `scaleX(${clamp((scroll + header - rootTop) / Math.max(1, rootHeight - viewport)).toFixed(5)})`;
    }

    function paintTheatre(record, progress) {
      const stamp = `${progress.toFixed(4)}:${record.imageReady}`;
      if (stamp === record.painted) return;
      record.painted = stamp;
      const state = photoState(progress, record.imageReady);
      const values = {
        '--cp-progress': progress, '--cp-setting-opacity': state.setting,
        '--cp-setting-scale': state.scale, '--cp-setting-copy': state.settingCopy,
        '--cp-front-enter': state.front, '--cp-front-copy': state.copy
      };
      Object.entries(values).forEach(([name, value]) => record.element.style.setProperty(name, value.toFixed(4)));
      record.element.dataset.caTheatreProgress = progress.toFixed(3);
      record.element.dataset.caTheatreState = state.phase;
      record.element.toggleAttribute('data-ca-theatre-fallback', !record.imageReady);
      record.element.toggleAttribute('data-ca-setting-exited', state.settingCopy < .001);
      setAccessible(record, record.setting, state.setting > .5 && state.front < .1);
      setAccessible(record, record.settingCopy, state.settingCopy > .1);
      setAccessible(record, record.front, state.front > .999);
      setAccessible(record, record.copy, state.copy > .999);
    }

    function update(time) {
      frame = 0;
      if (destroyed || document.hidden || !root.isConnected) return;
      if (dirty) measure();
      const scroll = window.scrollY;
      const elapsed = lastTime ? time - lastTime : 16.67;
      lastTime = time;
      let moving = false;
      updateChapter(scroll);
      const theatreActive = theatres.some(record => record.enabled && record.top < scroll + viewport && record.top + record.height > scroll + header + navHeight);
      // A theatre uses both permitted large planes. Elsewhere only the nearest
      // two visible depth photographs are explicitly promoted.
      let promotionBudget = theatreActive ? 0 : 2;
      depth.forEach(record => {
        const visible = record.top < scroll + viewport && record.top + record.height > scroll + header;
        const promoted = visible && promotionBudget >= record.planes.length;
        if (promoted) promotionBudget -= record.planes.length;
        record.element.classList.toggle('ca-m-promoted', promoted);
      });
      theatres.forEach(record => {
        if (!record.enabled) return;
        const visible = record.top < scroll + viewport && record.top + record.height > scroll + header + navHeight;
        record.element.classList.toggle('ca-cp-active', visible);
        const target = record.keyboardPhase ?? clamp((scroll + header + navHeight - record.top) / record.travel);
        record.value = visible ? approach(record.value, target, elapsed) : target;
        if (visible && Math.abs(record.value - target) >= .0004) moving = true;
        paintTheatre(record, record.value);
      });
      activeDepth.forEach(record => {
        if (!record.height || record.top + record.height < scroll - 150 || record.top > scroll + viewport + 150) return;
        const target = clamp((scroll + viewport - record.top) / Math.max(1, viewport + record.height));
        const progress = approach(record.value, target, elapsed);
        if (Math.abs(progress - target) >= .0004) moving = true;
        if (record.value !== null && Math.abs(progress - record.value) < .0001) return;
        record.value = progress;
        const range = Math.min(narrow ? 12 : record.range, record.height * .022);
        const y = (progress - .5) * range * 2;
        const scale = record.scale + (1 - ease(progress)) * (narrow ? .01 : .025);
        record.element.style.setProperty('--ca-m-y', `${y.toFixed(2)}px`);
        record.element.style.setProperty('--ca-m-scale', scale.toFixed(4));
        record.element.style.setProperty('--ca-m-aperture', `${((1 - ease(progress / .44)) * (narrow ? 2.5 : 5)).toFixed(3)}%`);
        if (record.detail) record.element.style.setProperty('--ca-m-detail-y', `${((.5 - progress) * (narrow ? 16 : 44)).toFixed(2)}px`);
        const scene = record.element.closest('.ca-f-scene');
        if (scene) scene.style.setProperty('--ca-m-scene-progress', clamp(progress / .7).toFixed(4));
      });
      alignments.forEach(record => {
        if (record.top > scroll + viewport + 100 || record.top + record.height < scroll - 100) return;
        const target = ease((scroll + viewport * .87 - record.top) / Math.max(1, viewport * .65));
        record.value = approach(record.value, target, elapsed);
        if (Math.abs(record.value - target) >= .0004) moving = true;
        const shift = ((1 - record.value) * (narrow ? 12 : 24)).toFixed(2);
        if (shift !== record.painted) { record.element.style.setProperty('--cp-detail-shift', `${shift}px`); record.painted = shift; }
      });
      if (!cinematic && opening && scroll < rootTop + viewport * 1.6) opening.style.setProperty('--ca-m-mat-y', `${(clamp((scroll - rootTop) / viewport) * (narrow ? 8 : 24)).toFixed(2)}px`);
      if (moving) frame = requestAnimationFrame(update);
      else lastTime = 0;
    }

    function queue(remeasure = false) {
      if (destroyed) return;
      if (remeasure) dirty = true;
      if (!frame && !document.hidden) frame = requestAnimationFrame(update);
    }

    function navigate(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      const link = event.target.closest('a[href^="#"]');
      if (!link || !root.contains(link) || link.matches('[data-ca-offer-open], [data-ca-thumbnail]')) return;
      if (!link.matches('[data-ca-section-link], .ca-f-stage-footer a, .ca-f-line-link, .ca-f-summary-link, .ca-pdp-summary-links a, .ca-f-mobile-choose a')) return;
      const id = link.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (!target || !root.contains(target) || target.matches('dialog')) return;
      event.preventDefault();
      settleWithin(target);
      if (dirty) measure();
      const destination = Math.max(0, target.getBoundingClientRect().top + window.scrollY - header - navHeight - 18);
      window.scrollTo({ top: destination, behavior: 'smooth' });
      history.replaceState(history.state, '', `#${id}`);
      if (event.detail === 0) {
        const heading = target.matches('h1,h2,summary') ? target : target.querySelector('h1,h2,summary');
        if (heading) { if (!heading.hasAttribute('tabindex') && heading.tagName !== 'SUMMARY') heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
      }
    }

    if ('IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver(entries => {
        entries.filter(entry => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top).forEach(entry => { const record = reveals.get(entry.target); if (record) reveal(record); });
      }, { threshold: .08, rootMargin: '0px 0px -4% 0px' });
      depthObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const record = depth.find(item => item.element === entry.target);
          if (!record) return;
          entry.target.classList.toggle('ca-m-in-view', entry.isIntersecting);
          if (entry.isIntersecting) activeDepth.add(record); else activeDepth.delete(record);
        });
        queue();
      }, { rootMargin: '180px 0px' });
      theatreObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const record = theatres.find(item => item.element === entry.target);
          if (record) primeTheatre(record);
          theatreObserver.unobserve(entry.target);
        });
      }, { rootMargin: '700px 0px' });
    }

    if (marker) { marker.className = 'ca-m-chapter-marker'; marker.setAttribute('aria-hidden', 'true'); rail.append(marker); }
    if (!cinematic) {
      register(root.querySelector('.ca-f-stage, .ca-pdp-gallery'), 'hero');
      register(root.querySelector('.ca-pdp-heading'), 'title');
      register(root.querySelector('[data-ca-purchase]'), 'purchase', 1);
    }
    register(root.querySelector('.ca-f-quick-facts'), 'copy', 2);
    all('[data-ca-f-reveal], [data-ca-reveal]').forEach((element, index) => {
      // Typeset product information must never acquire a cropped, faded or
      // moving photographic ancestor. Its text rules can arrive independently.
      if (element.closest('.ca-f-infographic-grid')) return;
      if (cinematic && element.matches('.ca-f-hardware')) { register(element.querySelector('figcaption'), 'copy'); return; }
      const heading = element.querySelector('h2');
      if (heading) {
        register(heading, 'heading');
        all(':scope > p, :scope > a, :scope > dl', element).forEach((child, i) => register(child, 'copy', i + 1));
      } else register(element, element.matches('figure') ? 'photo' : 'copy', index % 2);
    });
    all('.ca-f-visible-facts li, .ca-f-spec-list > div').forEach((element, index) => { element.dataset.caMRule = ''; register(element, 'rule', index % 4); });
    all('.ca-pdp-related-card').forEach((element, index) => register(element, 'photo', index % 3));
    all('.ca-collection-intro h1, .ca-page-heading h1').forEach(element => register(element, 'heading'));
    all('.ca-collection-intro__description, .ca-page-heading .ca-kicker, .ca-search-form, .ca-collection-help, .ca-search-suggestions > *').forEach((element, index) => register(element, 'copy', index % 3));
    all('.ca-catalog-card, .ca-search-editorial').forEach((card, index) => {
      register(card.querySelector('.ca-catalog-card__image') || card, 'photo', index % 2);
      register(card.querySelector('.ca-catalog-card__information'), 'copy', index % 2 + 1);
    });
    if (!cinematic) {
      addDepth('.ca-f-scene-photo', { aperture: true, range: 28 });
      addDepth('.ca-f-time-frame', { range: 18, scale: 1.045 });
      addDepth('.ca-f-timber-scene', { aperture: true, range: 26 });
      addDepth('.ca-f-hardware-photo', { range: 8, scale: 1.035, detail: true });
    }
    addDepth('.ca-f-close-photo', { range: 18, scale: 1.045 });
    addDepth('.ca-pdp-story-photos, .ca-pdp-story-inset', { range: 22 });
    addDepth('.ca-collection-intro figure', { range: 12, scale: 1.045 });
    registerTheatres();

    on(window, 'scroll', () => queue(), { passive: true });
    on(window, 'resize', () => queue(true), { passive: true });
    on(window, 'pageshow', () => queue(true));
    on(document, 'visibilitychange', () => {
      if (document.hidden) {
        if (frame) cancelAnimationFrame(frame);
        frame = 0; lastTime = 0;
        depth.forEach(record => record.element.classList.remove('ca-m-promoted'));
        theatres.forEach(record => record.element.classList.remove('ca-cp-active'));
      } else queue(true);
    });
    on(root, 'load', () => queue(true), { capture: true });
    on(root, 'click', navigate, { capture: true });
    on(root, 'focusin', event => settleWithin(event.target));
    on(rail, 'pointerdown', () => { manualRailUntil = performance.now() + 1800; }, { passive: true });
    on(rail, 'wheel', () => { manualRailUntil = performance.now() + 1800; }, { passive: true });
    on(rail, 'keydown', event => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || !event.target.matches('[data-ca-section-link]')) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const index = links.indexOf(event.target);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? links.length - 1 : clamp(index + (event.key === 'ArrowRight' ? 1 : -1), 0, links.length - 1);
      links[next]?.focus();
    });
    on(root, 'toggle', event => {
      if (!event.target.matches('details') || !event.target.open) return;
      [...event.target.children].filter(element => element.tagName !== 'SUMMARY').forEach(element => animate(element, [{ opacity: .5, translate: '0 -5px' }, { opacity: 1, translate: '0 0' }], { duration: 260 }));
      queue(true);
    }, { capture: true });
    // The store controller already owns filtering/sorting FLIP animations. Let it take over.
    const settleCatalog = event => {
      if (event.target.closest('[data-ca-filter], [data-ca-sort], [data-ca-local-search], [data-ca-reset-filters]')) {
        const grid = root.querySelector('.ca-catalog-grid');
        if (grid) settleWithin(grid);
      }
    };
    on(root, 'click', settleCatalog, { capture: true });
    on(root, 'input', settleCatalog, { capture: true });
    on(root, 'change', settleCatalog, { capture: true });
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => queue(true));
      [root, nav, ...bottomBars, ...depth.map(record => record.element), ...theatres.map(record => record.copy)].filter(Boolean).forEach(element => resizeObserver.observe(element));
    }
    mutationObserver = new MutationObserver(records => {
      const changesLayout = records.some(record => {
        if (record.target.nodeType === 1 && record.target.closest('[data-ca-sticky], [data-ca-mobile-choose]')) return record.attributeName === 'hidden';
        if (record.target.nodeType === 1 && record.target.closest('[data-ca-time-frame], [data-ca-media]')) return false;
        return record.type === 'childList' || record.attributeName === 'hidden' || record.attributeName === 'open';
      });
      if (changesLayout) queue(true);
    });
    mutationObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'open'] });
    root.dataset.caMotionReady = 'true';
    document.fonts?.ready.then(() => queue(true));
    queue(true);

    const destroy = () => {
      destroyed = true;
      life.abort();
      if (frame) cancelAnimationFrame(frame);
      revealObserver?.disconnect(); depthObserver?.disconnect(); theatreObserver?.disconnect(); resizeObserver?.disconnect(); mutationObserver?.disconnect();
      animations.forEach(running => running.forEach(animation => animation.cancel()));
      animations.clear();
      headings.forEach((html, heading) => { if (heading.isConnected) heading.innerHTML = html; });
      depth.forEach(({ element, planes }) => {
        element.classList.remove('ca-m-in-view', 'ca-m-promoted');
        delete element.dataset.caMDepth;
        ['--ca-m-y', '--ca-m-scale', '--ca-m-aperture', '--ca-m-detail-y'].forEach(name => element.style.removeProperty(name));
        planes.forEach(image => { delete image.dataset.caMPlane; });
        all('[data-ca-m-aperture]', element).forEach(picture => { delete picture.dataset.caMAperture; });
      });
      theatres.forEach(record => {
        record.element.classList.remove('ca-cp-active');
        ['ready', 'measuring', 'compact', 'fallback', 'state', 'progress'].forEach(key => record.element.removeAttribute(`data-ca-theatre-${key}`));
        record.element.removeAttribute('data-ca-setting-exited');
        [...record.element.style].filter(name => name.startsWith('--cp-')).forEach(name => record.element.style.removeProperty(name));
        record.accessibility.forEach((original, node) => {
          node.toggleAttribute('inert', original.inert);
          if (original.aria === null) node.removeAttribute('aria-hidden'); else node.setAttribute('aria-hidden', original.aria);
        });
      });
      alignments.forEach(record => record.element.style.removeProperty('--cp-detail-shift'));
      marker?.remove();
      root.style.removeProperty('--ca-m-nav-height');
      delete root.dataset.caMotionReady;
      delete root.dataset.caMotionActiveChapter;
      instances.delete(root);
    };
    instances.set(root, { destroy });
  }

  function init(context = document) {
    if (context.matches?.('[data-ca-site-motion]')) mount(context);
    context.querySelectorAll('[data-ca-site-motion]').forEach(mount);
  }
  window.CedarAmberMotion = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => instances.forEach((instance, root) => { if (event.target.contains(root)) instance.destroy(); }));
})();
